/**
 * Status-page illustrations: the picture each state a person can land on
 * leads with — signed out, outside their role, not found, our failure,
 * maintenance, offline, a module that is off, an expired link, too many
 * requests.
 *
 * The files are committed under `public/static/status/artwork/` and written by
 * `apps/api/scripts/generate-status-artwork.ts` (the role and Vishing artwork
 * recipe, with a scene brief). `lib/status/__tests__/status-artwork-assets.guard.spec.ts`
 * holds this list, the script's list, the files and their manifest together.
 *
 * The copy for each state lives under `shell.status.<copy key>` in
 * messages/shell/<locale>.json: title, body, alt, primary and (where the state
 * offers one) secondary.
 */

/** Every state, in the order the generator draws them. */
export const STATUS_KINDS = ['401', '403', '404', '500', '503', 'offline', 'module-off', 'expired', '429'] as const
export type StatusKind = (typeof STATUS_KINDS)[number]

/** The picture slugs: `status-<kind>`, one per state. */
export const STATUS_ARTWORK_SLUGS = STATUS_KINDS.map((kind) => `status-${kind}` as const)
export type StatusArtworkSlug = `status-${StatusKind}`

/** The picture size every file is written at (4:3). */
export const STATUS_ARTWORK_SIZE = { width: 1200, height: 900 } as const

const KIND_SET: ReadonlySet<string> = new Set(STATUS_KINDS)

export function isStatusKind(value: unknown): value is StatusKind {
  return typeof value === 'string' && KIND_SET.has(value)
}

/** The public path of a state's picture. Only a listed kind ever becomes a path. */
export function statusArtworkSrc(kind: StatusKind): string {
  if (!isStatusKind(kind)) throw new Error(`Unknown status kind: ${String(kind)}`)
  return `/static/status/artwork/status-${kind}.webp`
}

/**
 * The catalogue segment a state's copy lives under: `module-off` → `moduleOff`,
 * `404` → `404`. `shell.status.<segment>.title` and so on.
 */
export function statusCopySegment(kind: StatusKind): string {
  return kind.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

export type StatusCopyField = 'title' | 'body' | 'alt' | 'primary' | 'secondary'

/** `statusCopyKey('module-off', 'title')` → `shell.status.moduleOff.title`. */
export function statusCopyKey(kind: StatusKind, field: StatusCopyField): string {
  return `shell.status.${statusCopySegment(kind)}.${field}`
}

/** An HTTP status a person may quote to support; the other states have no code. */
export function statusCode(kind: StatusKind): string | null {
  return /^\d{3}$/.test(kind) && kind !== '401' ? kind : null
}

/**
 * Where "Back to Home" goes from a given address. The admin app's home is the
 * dashboard; the employee portal, the PhishTrainer game and the super-admin
 * console each have their own front page, and sending a learner to the admin
 * dashboard would only bounce them to a sign-in form they cannot use.
 */
export function statusHomeHref(pathname: string | null | undefined): string {
  const path = pathname ?? ''
  if (path === '/portal' || path.startsWith('/portal/')) return '/portal'
  if (path === '/play' || path.startsWith('/play/')) return '/play'
  if (path === '/admin' || path.startsWith('/admin/')) return '/admin'
  return '/dashboard'
}
